import { JSX, ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import AutorenewRoundedIcon from '@mui/icons-material/AutorenewRounded';
import moment from 'moment';
import Constants from 'style/constants';
import CopyButton from 'shared/components/buttons/copy-button/copy-button';
import SelectField from 'shared/components/formFields/select-field';
import ViewingModeController from 'shared/components/viewing-mode-controller/viewing-mode-controller';
import { DeviceType, G2DeviceTypes, isG2Device } from 'shared/models/device-type';

import useDeviceValidation from 'features/device/device-details/hooks/device-validation-hook';
import DeviceMonitoringAlerts from 'features/device/device-details/views/device-details-popup-information/components/device-monitoring-alert/device-monitoring-alert';
import DeviceSettingsType from 'features/device/device-details/views/device-details-settings/device-settings-type';

import 'features/device/device-details/views/device-details-popup-information/device-details-popup-information.scss';

import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import { InputLabelProps } from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import DesoExternalIdTextField from 'shared/components/deso-external-id-text-field/deso-external-id-text-field';
import G2ExternalIdTextField from 'shared/components/g2-external-id-text-field/g2-external-id-text-field';
import { RegexExpressions } from 'shared/constants/regex/regex-expressions';

import DesoDetectionLoopNumberTextField from 'features/device/device-details/views/device-details-popup-information/components/deso-detection-loop-number-text-field/deso-detection-loop-number-text-field';
import useDeviceDetailsPopupInformation from 'features/device/device-details/views/device-details-popup-information/hooks/use-device-details-popup-information';

const DeviceDetailsPopupInformation = (): JSX.Element => {
  const { t } = useTranslation('deviceDetails');

  const { validateName, validateExternalId, validateDetectionLoopNumber } = useDeviceValidation();
  const { currentDevice, currentViewingMode, currentDeviceType, gatewaysResponse, systemsResponse, form, generateExternalId, showField } =
    useDeviceDetailsPopupInformation();

  const registerOptions = {
    name: {
      required: t('info.requiredHelperText'),
      validate: validateName,
    },
    externalId: {
      required: t('info.requiredHelperText'),
      validate: validateExternalId,
    },
    gatewayId: {
      required: t('info.requiredHelperText'),
    },
    systemId: {
      required: t('info.requiredHelperText'),
    },
    detectionLoopNumber: {
      required: t('info.requiredHelperText'),
      validate: validateDetectionLoopNumber,
    },
    externalIdG2: {
      required: t('info.requiredHelperText'),
      validate: validateExternalId,
      pattern: {
        value: RegexExpressions.EXTERNAL_ID_G2,
        message: t('info.externalIdG2FormatHelperText'),
      },
    },
    externalIdDesoTransmitter: {
      required: t('info.requiredHelperText'),
      validate: validateExternalId,
      pattern: {
        value: RegexExpressions.EXTERNAL_ID_DESO_TRANSMITTER,
        message: t('info.externalIdDesoFormatHelperText'),
      },
    },
  };

  const isConnectedToSystem = currentDevice?.systemId;
  const isLinkedToSystem = currentDevice?.system != null;
  const isImported = currentDevice?.importDate != null;
  const shouldSelectSystem = currentDeviceType === DeviceType.DesoDetectionLoop;
  const shouldShowFunctionalNameField = currentDeviceType === DeviceType.DoorAlarm || currentDeviceType === DeviceType.DomipInput;

  const inputLabelProps: Partial<InputLabelProps> = isImported || isLinkedToSystem ? { shrink: true } : {};

  const deviceTypesToHideAddressField = [
    ...G2DeviceTypes,
    DeviceType.NFC,
    DeviceType.BLEBeacon,
    DeviceType.DesoResidentTransmitter,
    DeviceType.DesoSupervisorTransmitter,
    DeviceType.DesoDetectionLoop,
    DeviceType.DomipOutput,
    DeviceType.DomipInput,
    DeviceType.DoorAlarm,
  ];

  const getExternalIdRender = (): ReactElement => {
    if (!isLinkedToSystem && isG2Device(currentDeviceType as DeviceType)) {
      return (
        <div className="information-part-container" data-testid="devicePopupExternalIdContainer">
          <ViewingModeController
            viewingMode={currentViewingMode}
            name="externalId"
            control={form.control}
            rules={registerOptions.externalIdG2}
            label={t('info.externalId')}
            render={({ field }) => (
              <G2ExternalIdTextField field={field} errors={form.formState.errors} isImported={isImported} label={t('info.externalId')} />
            )}
            display={
              <div className="functional-id-container">
                <Typography variant="subtitle1" data-testid="viewingModeControllerDisplayValue">
                  {currentDevice?.externalId}
                </Typography>
                <CopyButton contentToCopy={currentDevice?.externalId} />
              </div>
            }
          />
        </div>
      );
    }

    if (currentDeviceType === DeviceType.DesoResidentTransmitter || currentDeviceType === DeviceType.DesoSupervisorTransmitter) {
      return (
        <div className="information-part-container" data-testid="devicePopupExternalIdContainer">
          <ViewingModeController
            viewingMode={currentViewingMode}
            name="externalId"
            control={form.control}
            rules={registerOptions.externalIdDesoTransmitter}
            label={t('info.externalId')}
            render={({ field }) => (
              <DesoExternalIdTextField field={field} errors={form.formState.errors} isImported={isImported} label={t('info.externalId')} />
            )}
            display={
              <div className="functional-id-container">
                <Typography variant="subtitle1" data-testid="viewingModeControllerDisplayValue">
                  {currentDevice?.externalId}
                </Typography>
                <CopyButton contentToCopy={currentDevice?.externalId} />
              </div>
            }
          />
        </div>
      );
    }

    return (
      <div className="information-part-container" data-testid="devicePopupExternalIdContainer">
        <ViewingModeController
          viewingMode={currentViewingMode}
          name="externalId"
          control={form.control}
          rules={registerOptions.externalId}
          label={t('info.externalId')}
          render={({ field }) => (
            <TextField
              {...field}
              slotProps={{
                input: {
                  endAdornment: currentDeviceType !== DeviceType.NFC && !isLinkedToSystem && (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => {
                          generateExternalId();
                          form.trigger('externalId');
                        }}
                        edge="end"
                      >
                        {currentDevice?.importDate == null && <AutorenewRoundedIcon />}
                      </IconButton>
                    </InputAdornment>
                  ),
                  disabled: isImported || isLinkedToSystem,
                },
              }}
              variant="outlined"
              id="externalIdInput"
              label={t('info.externalId')}
              error={!!form.formState.errors.externalId}
              helperText={form.formState.errors.externalId?.message?.toString()}
              className="form-field"
            />
          )}
          display={
            <div className="functional-id-container">
              <Typography variant="subtitle1" data-testid="viewingModeControllerDisplayValue">
                {currentDevice?.externalId}
              </Typography>
              <CopyButton contentToCopy={currentDevice?.externalId} />
            </div>
          }
        />
      </div>
    );
  };

  return (
    <div className="device-details-information-container">
      <Typography variant="h2" className="device-title" data-testid="devicePopupTitle">
        {currentViewingMode === 'creation' && t('createDeviceTitle')}
        {currentViewingMode === 'editing' && t('editDeviceTitle')}
        {currentViewingMode === 'viewing' && currentDevice?.name}
      </Typography>
      <DeviceMonitoringAlerts device={currentDevice} />
      <div className="information-part-container" data-testid="deviceTypeContainer">
        {currentViewingMode !== 'creation' && (
          <div className="icon-text">
            <Typography variant="h5">{t('info.typeLabel')}</Typography>
          </div>
        )}
        <DeviceSettingsType />
      </div>
      {currentDeviceType && currentDeviceType !== DeviceType.Unknown && (
        <>
          <div className="information-part-container">
            <ViewingModeController
              viewingMode={currentViewingMode}
              name="name"
              control={form.control}
              rules={registerOptions.name}
              label={t('info.nameLabel')}
              render={({ field }) => (
                <TextField
                  {...field}
                  slotProps={{
                    input: {
                      disabled: isImported,
                    },
                    htmlInput: {
                      'data-testid': 'nameInput',
                      maxLength: 255,
                    },
                  }}
                  id="nameInput"
                  label={t('info.nameLabel')}
                  variant="outlined"
                  error={!!form.formState.errors.name}
                  helperText={form.formState.errors.name?.message?.toString()}
                  className="form-field"
                />
              )}
            />
          </div>

          {showField('functionalName') && shouldShowFunctionalNameField && (
            <div className="information-part-container">
              <ViewingModeController
                viewingMode={currentViewingMode}
                name="functionalName"
                hideOnViewingWhenEmpty
                control={form.control}
                label={t('info.functionalNameLabel')}
                render={({ field }) => (
                  <TextField
                    {...field}
                    slotProps={{
                      input: {
                        disabled: isImported,
                      },
                      htmlInput: {
                        'data-testid': 'functionalNameInput',
                        maxLength: 255,
                      },
                    }}
                    id="functionalNameInput"
                    label={t('info.functionalNameLabel')}
                    variant="outlined"
                    className="form-field"
                  />
                )}
              />
            </div>
          )}

          {getExternalIdRender()}

          {isConnectedToSystem || shouldSelectSystem ? (
            <div className="information-part-container" data-testid="devicePopupSystemContainer">
              <ViewingModeController
                viewingMode={currentViewingMode}
                name="systemId"
                rules={registerOptions.systemId}
                control={form.control}
                displayValue={currentDevice?.system?.name ?? t('info.notFound')}
                label={t('info.systemLabel')}
                render={({ field }) => (
                  <SelectField
                    {...field}
                    value={field.value ?? ''}
                    disabled={isImported || currentViewingMode === 'editing'}
                    inputProps={{
                      'data-testid': 'systemInput',
                      disabled: isImported || currentViewingMode === 'editing',
                    }}
                    id="systemInput"
                    label={t('info.systemLabel')}
                    variant="outlined"
                    error={!!form.formState.errors.systemId}
                    helperText={form.formState.errors.systemId?.message?.toString()}
                    className="form-field"
                    data-testid="systemSelect"
                    onClick={() => form.trigger('systemId')}
                    renderValue={currentViewingMode === 'editing' ? (): string => currentDevice?.system?.name ?? '' : undefined}
                  >
                    {systemsResponse?.systems.map((system) => (
                      <MenuItem data-testid={`systemOption${system.id}`} key={`system-${system.id}`} value={system.id}>
                        {system.name}
                      </MenuItem>
                    ))}
                  </SelectField>
                )}
              />
            </div>
          ) : (
            <div className="information-part-container" data-testid="devicePopupGatewayContainer">
              <ViewingModeController
                viewingMode={currentViewingMode}
                name="gatewayId"
                rules={registerOptions.gatewayId}
                control={form.control}
                displayValue={currentDevice?.gateway?.name ?? t('info.notFound')}
                label={t('info.gatewayLabel')}
                render={({ field }) => (
                  <SelectField
                    {...field}
                    value={field.value ?? ''}
                    disabled={isImported || currentViewingMode === 'editing'}
                    inputProps={{
                      'data-testid': 'gatewayInput',
                      disabled: isImported || currentViewingMode === 'editing',
                    }}
                    id="gatewayInput"
                    label={t('info.gatewayLabel')}
                    variant="outlined"
                    error={!!form.formState.errors.gatewayId}
                    helperText={form.formState.errors.gatewayId?.message?.toString()}
                    className="form-field"
                    data-testid="gatewaySelect"
                    onClick={() => form.trigger('gatewayId')}
                  >
                    {gatewaysResponse?.gateways.map((gateway) => (
                      <MenuItem data-testid={`gatewayOption${gateway.id}`} key={`gateway-${gateway.id}`} value={gateway.id}>
                        {gateway.name}
                      </MenuItem>
                    ))}
                  </SelectField>
                )}
              />
            </div>
          )}

          {currentDeviceType === DeviceType.DesoDetectionLoop && (
            <div className="information-part-container" data-testid="devicePopupExternalIdContainer">
              <ViewingModeController
                viewingMode={currentViewingMode}
                name="detectionLoopNumber"
                control={form.control}
                rules={registerOptions.detectionLoopNumber}
                label={t('info.detectionLoopNumberLabel')}
                render={({ field }) => <DesoDetectionLoopNumberTextField field={field} errors={form.formState.errors} />}
              />
            </div>
          )}

          {showField('note') && (
            <div className="information-part-container" data-testid="devicePopupNoteContainer">
              <ViewingModeController
                viewingMode={currentViewingMode}
                name="note"
                control={form.control}
                label={t('info.noteLabel')}
                hideOnViewingWhenEmpty
                render={({ field }) => (
                  <TextField
                    {...field}
                    slotProps={{
                      htmlInput: {
                        'data-testid': 'noteInput',
                        maxLength: 255,
                      },
                    }}
                    id="noteInput"
                    label={t('info.noteLabel')}
                    variant="outlined"
                    error={!!form.formState.errors.note}
                    helperText={form.formState.errors.note?.message?.toString()}
                    multiline
                    minRows={8}
                    maxRows={12}
                    className="form-field"
                  />
                )}
              />
            </div>
          )}
          {currentViewingMode !== 'creation' &&
            currentDevice?.source &&
            currentDevice?.source !== undefined &&
            currentDevice?.source !== '' && (
              <div className="information-part-container" data-testid="devicePopupAddressContainer">
                <ViewingModeController
                  viewingMode={currentViewingMode}
                  name="source"
                  control={form.control}
                  label={t('info.sourceLabel')}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      slotProps={{
                        input: {
                          disabled: isImported || isLinkedToSystem,
                        },
                        htmlInput: {
                          'data-testid': 'sourceInput',
                          maxLength: 255,
                        },
                      }}
                      id="sourceInput"
                      label={t('info.sourceLabel')}
                      variant="outlined"
                      error={!!form.formState.errors.source}
                      helperText={form.formState.errors.source?.message?.toString()}
                      className="form-field"
                    />
                  )}
                />
              </div>
            )}
          {!deviceTypesToHideAddressField.includes(currentDeviceType) && (currentDevice?.address || currentViewingMode !== 'viewing') && (
            <div className="information-part-container" data-testid="devicePopupAddressContainer">
              <ViewingModeController
                viewingMode={currentViewingMode}
                hideOnViewingWhenEmpty
                name="address"
                control={form.control}
                label={t('info.addressLabel')}
                render={({ field }) => (
                  <TextField
                    {...field}
                    slotProps={{
                      inputLabel: inputLabelProps,
                      input: {
                        disabled: isImported || isLinkedToSystem,
                      },
                      htmlInput: {
                        'data-testid': 'addressInput',
                        maxLength: 255,
                      },
                    }}
                    id="addressInput"
                    label={t('info.addressLabel')}
                    variant="outlined"
                    error={!!form.formState.errors.address}
                    helperText={form.formState.errors.address?.message?.toString()}
                    className="form-field"
                  />
                )}
              />
            </div>
          )}
          {currentDevice?.deviceMonitoring?.lastMessageReceivedAt && (
            <div className="information-part-container">
              <Typography variant="h5" sx={{ color: Constants.Colors.onSurface }}>
                {t('info.status')}
              </Typography>
              <Typography variant="subtitle1" sx={{ color: Constants.Colors.onSurface }}>
                {`${t('info.lastSeenAt')} ${moment(currentDevice.deviceMonitoring.lastMessageReceivedAt)
                  .format('DD-MM-YYYY HH:mm')
                  .toString()}`}
              </Typography>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default DeviceDetailsPopupInformation;
